import { message } from "antd";
import { getCompanyById, getCompanyProducts, Product } from "./service";
import { searchPeople, fetchPerson as fetchPersonDetail, fetchPhone } from "../../Search/people/service";
import { Effect, Reducer, AnyAction } from '@umijs/max';
import { SimplifiedPerson } from "../../Search/people/model";
import { validateCredits } from "@/utils/credits";

interface CompanyDetailState {
    company: any | null;
    loading: boolean;
    peopleData: {
        pages: {
            [key: number]: {
                data: SimplifiedPerson[];
                total: number;
            }
        };
        total: number;
    };
    currentPeoplePage: number;
    peoplePageSize: number;
    productsData: Product[];
    productsLoading: boolean;
    activeTab: 'people' | 'map' | 'products' | 'org';
}

interface CompanyDetailModelType {
    namespace: 'companyDetail';
    state: CompanyDetailState;
    effects: {
        fetchCompany: Effect;
        fetchPeople: Effect;
        changePeoplePage: Effect;
        fetchPerson: Effect;
        fetchPhone: Effect;
        fetchProducts: Effect;
    };
    reducers: {
        updateState: Reducer<CompanyDetailState>;
        setCompany: Reducer<CompanyDetailState>;
        setPeopleForPage: Reducer<CompanyDetailState>;
        setActiveTab: Reducer<CompanyDetailState>;
        setProducts: Reducer<CompanyDetailState>;
        resetState: Reducer<CompanyDetailState>;
    };
}

const Model: CompanyDetailModelType = {
    namespace: 'companyDetail',
    state: {
        company: null,
        loading: false,
        peopleData: {
            pages: {},
            total: 0
        },
        currentPeoplePage: 1,
        peoplePageSize: 10,
        productsData: [],
        productsLoading: false,
        activeTab: 'people',
    },
    effects: {
        *fetchCompany({ payload }: AnyAction, { call, put }: any): Generator<any, void, any> {
            try {
                yield put({
                    type: 'updateState',
                    payload: { loading: true }
                });

                const company = yield call(getCompanyById, payload.id);

                yield put({
                    type: 'setCompany',
                    payload: company
                });

                yield put({
                    type: 'updateState',
                    payload: { loading: false }
                });

                // Auto-fetch people for this company
                if (company && company.id) {
                    yield put({
                        type: 'fetchPeople',
                        payload: { organizationId: company.id }
                    });
                }
            } catch (error) {
                yield put({
                    type: 'updateState',
                    payload: { loading: false }
                });
                message.error('Failed to fetch company details');
                console.error('Error fetching company:', error);
            }
        },
        *fetchPeople({ payload }: AnyAction, { call, put }: any): Generator<any, void, any> {
            try {
                const searchParams = {
                    organizations: [payload.organizationId],
                    page: 1,
                    role_is_current: true
                };

                const response = yield call(searchPeople, searchParams);

                yield put({
                    type: 'setPeopleForPage',
                    payload: {
                        page: 1,
                        data: response.search_results,
                        total: response.total_search_results
                    }
                });

                yield put({
                    type: 'updateState',
                    payload: { currentPeoplePage: 1 }
                });
            } catch (error) {
                message.error('Failed to fetch people data');
                console.error('Error fetching people:', error);
            }
        },
        *changePeoplePage({ payload }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {
                const state = yield select((state: any) => state.companyDetail);
                const targetPage = payload.page;

                // Check if page is already loaded
                if (state.peopleData.pages[targetPage]?.data?.length > 0) {
                    yield put({
                        type: 'updateState',
                        payload: { currentPeoplePage: targetPage }
                    });
                    return;
                }

                // Fetch new page
                const searchParams = {
                    organizations: [state.company.id],
                    page: targetPage,
                    role_is_current: true
                };

                const response = yield call(searchPeople, searchParams);

                yield put({
                    type: 'setPeopleForPage',
                    payload: {
                        page: targetPage,
                        data: response.search_results,
                        total: response.total_search_results
                    }
                });

                yield put({
                    type: 'updateState',
                    payload: { currentPeoplePage: targetPage }
                });
            } catch (error) {
                message.error('Failed to fetch people data for page');
                console.error('Error fetching people for page:', error);
            }
        },
        *fetchPerson({ callback, payload, isFreePlan }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {
                let state = yield select((state: any) => state.companyDetail);
                const { peopleData } = state;

                const isViewed = Object.values(peopleData.pages).some((pageData: any) => {
                    return pageData.data.some((person: SimplifiedPerson) =>
                        person.person_id === payload && person.viewed
                    );
                });

                if (isViewed) {
                    callback?.();
                    return;
                }

                const subscriptionState = yield select((state: any) => state.subscription);
                const { people_credits_remaining } = subscriptionState;
                
                if (!validateCredits(people_credits_remaining, 'person')) {
                    callback?.();
                    return;
                }

                const response: SimplifiedPerson = yield call(fetchPersonDetail, payload, isFreePlan);

                state = yield select((state: any) => state.companyDetail);
                const { currentPeoplePage } = state;
                const pageData = peopleData.pages[currentPeoplePage]?.data || [];

                const updatedPageData = pageData.map((person: SimplifiedPerson) => {
                    if (person.person_id === response.person_id) {
                        return {
                            ...person,
                            ...response,
                            viewed: true
                        };
                    }
                    return person;
                });

                yield put({
                    type: 'setPeopleForPage',
                    payload: {
                        page: currentPeoplePage,
                        data: updatedPageData,
                        total: peopleData.total
                    }
                });

                callback?.();

                if (response.work_email) {
                    yield put({
                        type: 'subscription/deductCredits',
                        payload: {
                            people_credits_remaining: people_credits_remaining - 5
                        }
                    });
                }
            } catch (error) {
                callback?.();
                message.error('Failed to fetch person data');
                console.error('Error fetching person:', error);
            }
        },
        *fetchPhone({ payload, callback }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {
                // If phone numbers already exist, no need to refetch
                if (payload.phone_numbers && payload.phone_numbers.length > 0) {
                    callback?.();
                    return;
                }

                const subscriptionState = yield select((state: any) => state.subscription);
                const { people_credits_remaining } = subscriptionState;
                
                if (!validateCredits(people_credits_remaining, 'phone')) {
                    callback?.();
                    return;
                }

                const response: any = yield call(fetchPhone, payload);

                if (response.success) {
                    let state = yield select((state: any) => state.companyDetail);
                    const { peopleData, currentPeoplePage } = state;
                    const pageData = peopleData.pages[currentPeoplePage]?.data || [];

                    const updatedPageData = pageData.map((person: SimplifiedPerson) => {
                        if (person.person_id === payload.person_id) {
                            return {
                                ...person,
                                phone_numbers: response.phone_numbers
                            };
                        }
                        return person;
                    });

                    yield put({
                        type: 'setPeopleForPage',
                        payload: {
                            page: currentPeoplePage,
                            data: updatedPageData,
                            total: peopleData.total
                        }
                    });

                    callback?.();

                    yield put({
                        type: 'subscription/deductCredits',
                        payload: {
                            people_credits_remaining: people_credits_remaining - 10
                        }
                    });
                } else {
                    message.error('No mobile number found');
                    callback?.();
                }
            } catch (error) {
                callback?.();
                message.error('No mobile number found');
                console.error('Error fetching phone:', error);
            }
        },
        *fetchProducts({ payload }: AnyAction, { call, put }: any): Generator<any, void, any> {
            try {
                yield put({
                    type: 'updateState',
                    payload: { productsLoading: true }
                });

                const products = yield call(getCompanyProducts, payload.foragerId);

                yield put({
                    type: 'setProducts',
                    payload: products
                });

                yield put({
                    type: 'updateState',
                    payload: { productsLoading: false }
                });
            } catch (error) {
                yield put({
                    type: 'updateState',
                    payload: { productsLoading: false }
                });
                message.error('Failed to fetch products');
                console.error('Error fetching products:', error);
            }
        }
    },
    reducers: {
        updateState(state: CompanyDetailState, { payload }: AnyAction): CompanyDetailState {
            return {
                ...state,
                ...payload
            };
        },
        setCompany(state: CompanyDetailState, { payload }: AnyAction): CompanyDetailState {
            return {
                ...state,
                company: payload
            };
        },
        setPeopleForPage(state: CompanyDetailState, { payload }: AnyAction): CompanyDetailState {
            const { page, data, total } = payload;

            return {
                ...state,
                peopleData: {
                    ...state.peopleData,
                    pages: {
                        ...state.peopleData.pages,
                        [page]: {
                            data,
                            total
                        }
                    },
                    total
                }
            };
        },
        setActiveTab(state: CompanyDetailState, { payload }: AnyAction): CompanyDetailState {
            return {
                ...state,
                activeTab: payload
            };
        },
        setProducts(state: CompanyDetailState, { payload }: AnyAction): CompanyDetailState {
            return {
                ...state,
                productsData: payload
            };
        },
        resetState(): CompanyDetailState {
            return {
                company: null,
                loading: false,
                peopleData: {
                    pages: {},
                    total: 0
                },
                currentPeoplePage: 1,
                peoplePageSize: 10,
                productsData: [],
                productsLoading: false,
                activeTab: 'people',
            };
        }
    }
};

export default Model;
